import React, {useState, useRef, useEffect, useContext} from "react";

import {useHistory} from "react-router-dom";
import {format, isSameDay, parseISO} from "date-fns";
import openSocket from "socket.io-client";
import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import {makeStyles} from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import NotificationsIcon from '@material-ui/icons/Notifications';

import alertSound from "../../assets/sound.mp3";
import skypeSound from "../../assets/skype.mp3";
import experienceSound from "../../assets/experience_orbs.mp3";
import blubSound from "../../assets/blub.mp3";
import {AuthContext} from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Typography from "@material-ui/core/Typography";
import MarkdownWrapper from "../MarkdownWrapper";
import {toast} from "react-toastify";
import {Button} from "@material-ui/core";

const useStyles = makeStyles(theme => ({
    iconButton: {
        padding: 10,
    },

    badge: {
        "& .MuiBadge-badge": {
            minWidth: 18,
            height: 18,
            padding: "0 5px",
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 10,
            // boxShadow: "0 0 0 2px rgba(255,255,255,0.95)",
        },
    },

    popoverPaper: {
        width: "100%",
        maxWidth: 380,
        marginTop: theme.spacing(1),
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        border: "1px solid rgba(0,0,0,0.08)",
        [theme.breakpoints.down("sm")]: {
            maxWidth: 300,
        },
    },

    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        background: theme.palette.background.paper,
    },

    headerTitle: {
        fontWeight: 700,
        fontSize: 15,
    },

    headerCount: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        background: "rgba(0,0,0,0.05)",
        padding: "4px 8px",
        borderRadius: 999,
    },

    tabContainer: {
        maxHeight: 380,
        overflowY: "auto",
        padding: 8,
        background: "#fafafa",
        ...theme.scrollbarStyles,
    },

    emptyState: {
        padding: "28px 18px",
        textAlign: "center",
        color: theme.palette.text.secondary,
    },

    notificationItem: {
        padding: 0,
        borderRadius: 12,
        marginBottom: 8,
        overflow: "hidden",
        background: theme.palette.background.paper,
        border: "1px solid rgba(0,0,0,0.06)",
        transition: "all 0.2s ease",
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
            background: "#fff",
        },
        "&:last-child": {
            marginBottom: 0,
        },
    },

    notificationButton: {
        alignItems: "flex-start",
        padding: "12px 14px",
    },

    notificationContent: {
        width: "100%",
        minWidth: 0,
    },

    topRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 6,
    },

    title: {
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        minWidth: 0,
        flex: 1,
    },

    time: {
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        background: "rgba(0,0,0,0.05)",
        padding: "2px 8px",
        borderRadius: 999,
    },

    message: {
        fontSize: 13,
        lineHeight: 1.45,
        color: theme.palette.text.secondary,
        overflow: "hidden",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        whiteSpace: "pre-line",
        "& p": {
            margin: 0,
        },
        "&, & *": {
            maxWidth: "100%",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            whiteSpace: "inherit",
        },
        "& a": {
            display: "inline",
        },
    },

    unreadDot: {
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: theme.palette.primary.main,
        marginTop: 6,
        marginRight: 10,
        flexShrink: 0,
    },

    itemInner: {
        display: "flex",
        width: "100%",
        minWidth: 0,
    },
}));

const NotificationsUserPopOver = () => {
    const classes = useStyles();

    const history = useHistory();
    const {user} = useContext(AuthContext);
    const ticketIdUrl = +history.location.pathname.split("/")[2];
    const ticketIdRef = useRef(ticketIdUrl);
    const anchorEl = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);

    const [, setDesktopNotifications] = useState([]);

    const [play] = useSound(alertSound);
    const [playExp] = useSound(experienceSound);
    const [playBlub] = useSound(blubSound);
    const [playSkype] = useSound(skypeSound);
    const soundAlertRef = useRef();

    // const historyRef = useRef(history);

    useEffect(() => {
        (async () => {
            try {
                const {data} = await api.get("/settings");

                const nat = data.find(s => s.key === "sound")
                if (nat?.value) {
                    switch (nat?.value) {
                        case "experience":
                            soundAlertRef.current = playExp;
                            break;
                        case "blub":
                            soundAlertRef.current = playBlub;
                            break;
                        case "skype":
                            soundAlertRef.current = playSkype;
                            break;
                        default:
                            soundAlertRef.current = play;
                            break;
                    }
                } else {
                    soundAlertRef.current = play;
                }
            } catch (err) {
                soundAlertRef.current = play;
                toastError(err);
            }
        })();


        /*if (!("Notification" in window)) {
            console.log("This browser doesn't support notifications");
        } else {
            Notification.requestPermission();
        }*/
    }, [play, playExp, playBlub, playSkype]);

    useEffect(() => {
        (async () => {
            try {
                const {data} = await api.get("/notification/" + user.id);

                if (data)
                    setNotifications(data);
            } catch (err) {
                toastError(err);
            }
        })();
    }, [user]);

    useEffect(() => {
        ticketIdRef.current = ticketIdUrl;
    }, [ticketIdUrl]);

    useEffect(() => {
        const socket = openSocket(window.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL, {auth: {token: localStorage.getItem("socketToken")}});

        socket.on("connect", () => socket.emit("joinUserNotification", user.id));

        socket.on("notificate", data => {
            toast.info("Nova notificação recebida!");
            handleNotifications(data);

            setNotifications(prev => {
                const index = prev.findIndex(n => n.id === data.id);

                let updatedList;

                if (index !== -1) {
                    // 🔄 atualiza existente
                    updatedList = [...prev];
                    updatedList[index] = {
                        ...updatedList[index],
                        ...data,
                    };
                } else {
                    // ➕ adiciona novo no topo
                    updatedList = [data, ...prev];
                }

                // 🧠 ordena por data (mais recente primeiro)
                updatedList.sort((a, b) => {
                    // 1️⃣ read ASC (false primeiro)
                    if (a.read !== b.read) {
                        return Number(a.read) - Number(b.read);
                    }

                    // 2️⃣ createdAt DESC
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });

                return updatedList;
            });
        });

        socket.on("notificationUpdate", data => {
            setNotifications(prev => {
                const index = prev.findIndex(n => n.id === data.id);
                if (index === -1) return prev;

                const updatedList = [...prev];
                updatedList[index] = {
                    ...updatedList[index],
                    ...data,
                };

                updatedList.sort((a, b) => {
                    // 1️⃣ read ASC (false primeiro)
                    if (a.read !== b.read) {
                        return Number(a.read) - Number(b.read);
                    }

                    // 2️⃣ createdAt DESC
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });

                return updatedList;
            });
        });
        return () => {
            socket.disconnect();
        };
    }, [user]);

    const handleNotifications = data => {
        const {title, message, link, id} = data;

        const options = {
            data: {
                title: title,
                url: link
            },
            body: `${message} - ${format(new Date(), "HH:mm")}`,
            icon: "./144.png",
            tag: "notification_" + id,
            renotify: true,
        };

        soundAlertRef.current();

        const notification = {};

        if (navigator.serviceWorker)
            navigator.serviceWorker.controller.postMessage(options)

        setDesktopNotifications(prevState => {
            const notfiticationIndex = prevState.findIndex(
                n => n.tag === notification.tag
            );
            if (notfiticationIndex !== -1) {
                prevState[notfiticationIndex] = notification;
                return [...prevState];
            }
            return [notification, ...prevState];
        });
    };

    const handleClick = () => {
        setIsOpen(prevState => !prevState);
    };

    const handleClickAway = () => {
        setIsOpen(false);
    };

    const NotificationFragment = ({children}) => {
        return <div onClick={handleClickAway}>{children}</div>;
    };

    const handleClickNotification = async notify => {
        await api.put("/notification/read/" + notify.id, notify);

        if (notify.link) {
            if (notify.link.includes("http"))
                window.open(notify.link, "_blank");
            else
                history.push(notify.link);
        }
        handleClickAway();
    }

    const handleReadAll = async () => {
        try {
            await api.put("/notification/read-all/" + user.id);

            setNotifications(prev => {
                const updatedList = prev.map(notification => ({
                    ...notification,
                    read: true,
                    readAt: new Date().toISOString(),
                }));

                updatedList.sort((a, b) => {
                    if (a.read !== b.read) {
                        return Number(a.read) - Number(b.read);
                    }

                    return new Date(b.createdAt) - new Date(a.createdAt);
                });

                return updatedList;
            });
        } catch (err) {
            toastError(err);
        }
    };

    const normalizeNotificationMessage = message => {
        if (typeof message !== "string") {
            return message;
        }

        return message
            .replace(/\\r\\n/g, "\n")
            .replace(/\\n/g, "\n")
            .replace(/\r\n/g, "\n")
            .replace(/\n/g, "  \n");
    };

    return (
        <>
            <IconButton
                onClick={handleClick}
                ref={anchorEl}
                aria-label="Abrir notificações"
                color="inherit"
                className={classes.iconButton}
            >
                <Badge
                    overlap="rectangular"
                    badgeContent={notifications.filter(n => !n.read).length}
                    color="secondary"
                    className={classes.badge}
                >
                    <NotificationsIcon/>
                </Badge>
            </IconButton>

            <Popover
                disableScrollLock
                open={isOpen}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                classes={{paper: classes.popoverPaper}}
                onClose={handleClickAway}
            >
                <div className={classes.header}>
                    <Typography className={classes.headerTitle}>
                        Notificações
                    </Typography>

                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                        <Button
                            size="small"
                            color="primary"
                            onClick={handleReadAll}
                            disabled={!notifications.some(n => !n.read)}
                        >
                            Marcar todas como lidas
                        </Button>

                        <span className={classes.headerCount}>
                            {notifications.length}
                        </span>
                    </div>

                </div>

                <List dense className={classes.tabContainer}>
                    {notifications.length === 0 ? (
                        <div className={classes.emptyState}>
                            <Typography variant="body2">
                                Nenhuma notificação encontrada
                            </Typography>
                        </div>
                    ) : (
                        notifications.map(notify => (
                            <NotificationFragment key={notify.id}>
                                <ListItem
                                    button
                                    className={classes.notificationItem}
                                    onClick={() => handleClickNotification(notify)}
                                >
                                    <div className={classes.notificationButton}>
                                        <div className={classes.itemInner}>
                                            { !notify.read && (
                                              <div className={classes.unreadDot}/>
                                            )}

                                            <div className={classes.notificationContent}>
                                                <div className={classes.topRow}>
                                                    <Typography
                                                        className={classes.title}
                                                        noWrap
                                                    >
                                                        {notify.title}
                                                    </Typography>

                                                    <span className={classes.time}>
                                                    {isSameDay(parseISO(notify.updatedAt), new Date())
                                                        ? format(parseISO(notify.updatedAt), "HH:mm")
                                                        : format(parseISO(notify.updatedAt), "dd/MM")}
                                                </span>
                                                </div>

                                                <Typography
                                                    className={classes.message}
                                                    component="div"
                                                >
                                                    <MarkdownWrapper>
                                                        {normalizeNotificationMessage(notify.message)}
                                                    </MarkdownWrapper>
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </ListItem>
                            </NotificationFragment>
                        ))
                    )}
                </List>
            </Popover>
        </>
    );
};

export default NotificationsUserPopOver;
